import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteMenu } from '../hooks/useMenu';

/**
 * Modal component for confirming menu deletion
 * Shows warning about cascade deletion and provides confirmation buttons
 */
const DeleteMenuModal = ({ 
  isOpen = true, // Default to true if not provided
  onClose, 
  onSuccess,
  menuId
}) => {
  const { t } = useTranslation();
  
  // Use the React Query hook for menu deletion
  const deleteMenuMutation = useDeleteMenu();
  
  // Component state
  const [error, setError] = useState(null);
  
  // Handle delete menu request
  const handleDeleteMenu = async () => {
    if (!menuId) {
      setError('Menu ID is missing');
      return;
    }
    
    try {
      setError(null);
      
      console.log(`[DEBUG-DeleteMenu] Starting deletion of menu ID: ${menuId} at ${new Date().toISOString()}`);
      
      // Use mutation to delete and invalidate caches
      await deleteMenuMutation.mutateAsync(menuId);
      
      console.log(`[DEBUG-DeleteMenu] Successfully deleted menu ID: ${menuId} at ${new Date().toISOString()}`);
      
      // Close modal and notify parent of success
      onClose();
      if (onSuccess) {
        console.log(`[DEBUG-DeleteMenu] Calling onSuccess callback at ${new Date().toISOString()}`);
        onSuccess();
      }
    } catch (err) {
      console.error(`[DEBUG-DeleteMenu] Failed to delete menu: ${err} at ${new Date().toISOString()}`);
      setError('Failed to delete menu. Please try again later.');
    }
  };
  
  if (isOpen === false) return null;
  
  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        
        <div 
          className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                  {t('modal.confirmDeleteMenu')}
                </h3>
                <div className="mt-2">
                  {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {error}
                    </div>
                  )}
                  
                  {deleteMenuMutation.isPending ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-500 mb-2">
                        {t('modal.deleteMenuWarning')}
                      </p>
                      <p className="text-sm text-red-600 font-medium">
                        {t('modal.actionCannotBeUndone')}
                      </p>
                      <ul className="mt-2 text-sm text-gray-500 list-disc pl-5 space-y-1">
                        <li>{t('modal.allMenuDaysWillBeDeleted')}</li>
                        <li>{t('modal.allMenuItemsWillBeDeleted')}</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              onClick={handleDeleteMenu}
              disabled={deleteMenuMutation.isPending}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {deleteMenuMutation.isPending ? t('admin.deleting') : t('modal.deleteMenu')}
            </button>
            <button 
              type="button" 
              onClick={onClose}
              disabled={deleteMenuMutation.isPending}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteMenuModal;




